/**
 * `<ThemeProvider>` — applies the `useUiStore` theme to the document root.
 *
 * Two modes:
 *
 * 1. **Built-in theme** (`ember` / `blue-slate` / `parchment` / `slate` / etc.) —
 *    written as a `data-theme` attribute on `<html>`. tokens.css's
 *    `:root[data-theme='…']` block matches and supplies every CSS variable.
 *
 * 2. **Custom theme** (`theme === 'custom'`) — 2026-06-01 (Chris ask). Reads the
 *    user-authored theme from the `useCustomThemesQuery` cache, then applies its
 *    curated token map (~13 keys) via `style.setProperty()` on `<html>`. The
 *    universal `:root` color-mix block in tokens.css derives the rest of the
 *    vocabulary (--accent-soft, --accent-line, --bg-app, --primary-soft, etc.)
 *    from those base values. `data-theme` is also set to `"custom"` so any CSS
 *    that wants to distinguish "this is a user theme" can check.
 *
 * The system mode resolves to ember (dark OS pref) or parchment (light OS pref).
 *
 * **Flicker guard.** When the theme changes, `transition: all` on any element can
 * stick on the old palette for one frame, producing a visible flash. The fix is to
 * suppress transitions for one frame around the swap by toggling a
 * `[data-theme-switching]` attribute on `<html>` — tokens.css contains the matching
 * `transition: none !important` rule. The attribute is cleared on the next-next
 * frame (double rAF) so transitions resume immediately after the swap.
 */

import { useEffect, type ReactNode } from 'react';
import { useUiStore } from '@/stores';
import { useCustomThemesQuery } from '@/hooks/useCustomThemes';

interface ThemeProviderProps {
  children: ReactNode;
}

/** Apply (or clear) the curated CSS-var overrides for a custom theme. */
function applyCustomTokens(
  root: HTMLElement,
  tokens: Record<string, string> | null,
): void {
  // Track which custom keys we've stamped so we can clean them up on switch
  // away. We stash the key list in a data-attribute on <html> — survives
  // re-renders and StrictMode double-effect-invoke without leaking refs.
  const STASH = 'customThemeAppliedKeys';
  const prevKeys: string[] = (() => {
    const raw = root.dataset[STASH];
    if (!raw) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
    } catch {
      return [];
    }
  })();
  // Clear previous overrides — important when switching from one custom theme
  // to another, or back to a built-in.
  for (const key of prevKeys) {
    root.style.removeProperty(`--${key}`);
  }
  if (tokens === null) {
    delete root.dataset[STASH];
    return;
  }
  // Apply each token in the new map.
  const nextKeys: string[] = [];
  for (const [key, value] of Object.entries(tokens)) {
    if (typeof key === 'string' && typeof value === 'string' && value.length > 0) {
      root.style.setProperty(`--${key}`, value);
      nextKeys.push(key);
    }
  }
  root.dataset[STASH] = JSON.stringify(nextKeys);
}

export function ThemeProvider({ children }: ThemeProviderProps): JSX.Element {
  const theme = useUiStore((s) => s.theme);
  const activeCustomThemeId = useUiStore((s) => s.activeCustomThemeId);

  // Pull custom themes from cache. The Themes settings tab keeps this fresh;
  // here we're a read-only consumer. `data` is `undefined` on first load — we
  // still set data-theme="custom" so flicker is minimized; the token overrides
  // land once data arrives.
  const customThemes = useCustomThemesQuery();
  const activeCustomTheme =
    theme === 'custom' && activeCustomThemeId !== null
      ? customThemes.data?.find((t) => t.id === activeCustomThemeId) ?? null
      : null;
  // Stable key for the effect — re-applies when tokens change (user edits in
  // the Themes tab, SSE-pushed update from another device, etc).
  const customTokenSig = activeCustomTheme
    ? JSON.stringify(activeCustomTheme.tokens)
    : null;

  useEffect(() => {
    const root = document.documentElement;

    const apply = (): void => {
      // Resolve the data-theme attribute.
      let resolved: string;
      if (theme === 'system') {
        resolved = window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'ember'
          : 'parchment';
      } else if (theme === 'custom') {
        // Set data-theme="custom" so any CSS that scopes by theme can check;
        // the actual values come from the inline style.setProperty calls
        // below. If we have a seed_theme_id, ALSO set it so the chrome
        // overrides keyed on the seed (e.g. _theme-chrome-overrides.css pale-
        // theme rules) still apply when the user clones a light theme.
        resolved = activeCustomTheme?.seedThemeId ?? 'custom';
      } else {
        resolved = theme;
      }

      // Flicker guard.
      root.setAttribute('data-theme-switching', '');
      root.setAttribute('data-theme', resolved);

      // Custom-theme overrides — apply or clear depending on mode.
      applyCustomTokens(root, activeCustomTheme?.tokens ?? null);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.removeAttribute('data-theme-switching');
        });
      });
    };

    apply();

    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, activeCustomThemeId, customTokenSig]);

  return <>{children}</>;
}
